import { GraphTemplateV2 } from "app/components/widget/dashboard/GraphTemplate-v2";
import { fetchWithFullResponseInterceptorHandlers } from "app/components/widget/widget-fetchers/DefaultDataFetcher";
import { percentageSignFilter } from "app/filters/ngFilters";
import { adaptDataForTicks } from "app/pages/app-intelligence/app-analysis/pages/Retention/configs/retentionOverTimeGraphConfig";
import { AppRetentionApiService } from "app/pages/app-intelligence/app-analysis/pages/Retention/services/AppRetentionApiService";
import { transformOverTimeData } from "app/pages/app-intelligence/app-analysis/pages/Retention/utilities/transformOverTimeData";

import { GraphWidget } from "./GraphWidget";

export class AppRetentionDashboardGraphWidget extends GraphWidget {
    public isPptSupported = () => {
        return false;
    };

    constructor() {
        super();
    }

    static getWidgetMetadataType() {
        return "AppRetentionDashboardGraph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    get templateComponent() {
        return GraphTemplateV2;
    }

    get templateUrl() {
        return `/app/components/widget/widget-templates/graph.html`;
    }

    private dataFetcherFactory = () => {
        return {
            create: (widget) => {
                return {
                    fetch: () => {
                        const { from, to } = widget.apiParams;
                        const apiParams = {
                            ...widget.apiParams,
                            ...AppRetentionApiService.getDurationValuesForChartRequest(from, to),
                        };
                        return AppRetentionApiService.getChartData(apiParams)
                            .then(...fetchWithFullResponseInterceptorHandlers)
                            .then((rawData: any) => ({
                                Data: rawData,
                            }));
                    },
                };
            },
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    protected validateData(data: any) {
        return Object.values(data.AppsRetention).some((val) => !!val);
    }

    public getXAxisTickInterval() {
        return 1;
    }

    public getYAxisPrecision(series = []) {
        return 2;
    }

    public getLegendItems(data?: any) {
        return data?.length
            ? [...this._widgetConfig.properties.key, data[data.length - 1]]
            : this._widgetConfig.properties.key;
    }

    private addSeriesNames(chartData) {
        if (!chartData) {
            return;
        }
        chartData.forEach((chartDataItem) => {
            const keyObject = this._widgetConfig.properties.key.find((key) => key.id === chartDataItem.id);
            if (keyObject) {
                chartDataItem.name = keyObject.name;
            }
            if (chartDataItem.categoryBenchmark) {
                chartDataItem.name = `${this._i18nFilter("app.analysis.retention.overtime.legend.category.prefix")} ${
                    chartDataItem.name
                }`;
            }
        });
        return chartData;
    }

    protected async getChartSeries(unorderedData: any, compareItemsKeys: string[]): Promise<any[]> {
        const { chartData } = transformOverTimeData(unorderedData);
        const seriesItemsWithNames = this.addSeriesNames(chartData);
        this.legendItems = this.getLegendItems(seriesItemsWithNames);
        return seriesItemsWithNames;
    }

    public getHighChartsConfig(chartOptions, formattedData, data) {
        const translate = this._i18nFilter;
        const adaptedDataForTicks = adaptDataForTicks(formattedData);
        const adaptedTickPositions = adaptedDataForTicks
            .find((dataObj) => dataObj.data?.length > 0)
            .data.map(([key]) => key);
        const daysToDisplay = formattedData?.length
            ? formattedData.find((dataObj) => dataObj.data?.length > 0).data.map((dataPoint) => dataPoint[0])
            : [];
        const updatedChartOptions = {
            ...chartOptions,
            tooltip: {
                formatter() {
                    const { points } = this;
                    const index = points[0].series.indexOf(this.x);
                    const day = daysToDisplay[index];
                    const header = translate("app.analysis.retention.historical.table.column.day", {
                        day,
                    });
                    const lines = [];
                    lines.push("<div class='date'>" + header + "</div>" + '<div class="line-seperator"></div>');
                    this.points?.forEach((point) => {
                        lines.push(
                            "<div>" +
                                '<span class="item-marker" style="background: ' +
                                point.series.color +
                                '"></span>' +
                                '<span class="item-name">' +
                                point.series.name +
                                '<span class="item-value" style="margin-left:4px;color: ' +
                                point.series.color +
                                ';">' +
                                percentageSignFilter()(point.point.y, 0) +
                                "</span></span></div>",
                        );
                    });
                    return lines.join("");
                },
            },
            xAxis: {
                startOnTick: true,
                endOnTick: true,
                tickPositions: adaptedTickPositions,
            },
            xAxisLabelFormatter: function () {
                const index = adaptedTickPositions.indexOf(this.value);
                const day = daysToDisplay[index];
                return translate("app.analysis.retention.historical.table.column.day", {
                    day,
                });
            },
        };
        return super.getHighChartsConfig(updatedChartOptions, adaptedDataForTicks, data);
    }

    public getProUrl(rowParams) {
        const apiParams = Object.assign(this._getProUrlParams(), this._metricConfig.stateParams, rowParams);

        return this._swNavigator.href(this._metricConfig.state, {
            ...apiParams,
        });
    }

    public async callbackOnGetData(response: any, comparedItemKeys?: any[]): Promise<void> {
        await super.callbackOnGetData(response, comparedItemKeys);
    }
}
AppRetentionDashboardGraphWidget.register();
